import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';

/**
 * Se abre cuando el servidor rechaza la sesión en medio del uso. Pide solo la
 * contraseña, sin salir de la pantalla: lo que se estaba cargando queda
 * intacto y, al entrar, se guarda solo. Quien entra con código por mail puede
 * hacerlo en otra pestaña: esta lo detecta y sigue.
 */
export default function ModalSesionVencida() {
  const { user, sesionVencida, reingresar, logout } = useAuth();
  const [clave, setClave] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  if (!user || !sesionVencida) return null;

  const entrar = async (e) => {
    e.preventDefault();
    if (!clave) return;
    setEnviando(true);
    setError(null);
    try {
      await reingresar(clave);
      setClave('');
    } catch (err) {
      setError(err?.message || 'No se pudo entrar. Revisá la contraseña.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <AlertDialog open>
      <AlertDialogContent data-testid="modal-sesion-vencida">
        <AlertDialogHeader>
          <AlertDialogTitle>Tu sesión se venció</AlertDialogTitle>
          <AlertDialogDescription>
            Ingresá de nuevo para seguir. <strong>Lo que estabas cargando no se pierde</strong>:
            si estabas guardando, se guarda solo apenas entres.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={entrar} className="space-y-3">
          <Input value={user.email} disabled className="bg-slate-50" />
          <Input
            type="password"
            placeholder="Contraseña"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            autoFocus
            data-testid="modal-sesion-clave"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" className="w-full" disabled={enviando || !clave}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ingresar y seguir'}
          </Button>
          <p className="text-xs text-slate-500 text-center">
            ¿Entrás con código o no te acordás la contraseña? Hacelo{' '}
            <a href="/auth/otp" target="_blank" rel="noopener noreferrer" className="underline">
              en otra pestaña
            </a>
            , sin cerrar esta: cuando entres allá, esta sigue sola.
          </p>
          <button
            type="button"
            onClick={async () => { await logout(); window.location.assign('/login'); }}
            className="block mx-auto text-xs text-slate-400 hover:text-slate-600 underline"
          >
            Salir y entrar con otra cuenta (se pierde lo que no guardaste)
          </button>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
